import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import { take } from 'rxjs/operators'
import { caseActions } from '../../state/case/case.actions'
import { selectionActions } from '../../state/selection/selection.actions'
import { plotDataActions } from '../../state/plot-data/plot-data.actions'
import { SegObjectPropertyMap, CaseId } from '../../state/case/case.state'
import {
    selectHasSegObjectPropertyMaps,
    selectCaseSegObjectPropertyMapsForLoadedSegLayers,
    selectCaseId,
    selectLoadedSegObjectPropertyMaps,
} from '../../state/case/case.selectors'

@Component({
    selector: 'app-segobjectpropertymap-panel',
    templateUrl: './segobjectpropertymap-panel.component.html',
    styleUrls: ['./segobjectpropertymap-panel.component.scss'],
})
export class SegObjectPropertyMapPanelComponent {
    @ViewChild('dropdownRoot') dropdownRoot?: ElementRef<HTMLElement>

    /** When true, at most one intensity map may be loaded (e.g. plotting). */
    @Input() singleSelection = false

    readonly hasSegObjectPropertyMaps$ = this.store.select(selectHasSegObjectPropertyMaps)
    readonly segObjectPropertyMaps$ = this.store.select(
        selectCaseSegObjectPropertyMapsForLoadedSegLayers
    )
    readonly loadedSegObjectPropertyMaps$ = this.store.select(selectLoadedSegObjectPropertyMaps)

    dropdownOpen = false

    constructor(private readonly store: Store) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.dropdownOpen) return
        const root = this.dropdownRoot?.nativeElement
        if (root && !root.contains(event.target as Node)) {
            this.dropdownOpen = false
        }
    }

    toggleDropdown(event: MouseEvent): void {
        event.stopPropagation()
        this.dropdownOpen = !this.dropdownOpen
    }

    isMapLoaded(mapId: number, loadedMaps: SegObjectPropertyMap[]): boolean {
        return loadedMaps.some(map => map.id === mapId)
    }

    /**
     * Another loaded map already uses this map's segSource — only one map per
     * segmentation can be selected (selection buckets are keyed by segSource).
     */
    isMapDisabledBySegSource(
        map: SegObjectPropertyMap,
        loadedMaps: SegObjectPropertyMap[]
    ): boolean {
        if (!map.segSource) return false
        if (this.isMapLoaded(map.id, loadedMaps)) return false
        return loadedMaps.some(
            loaded => loaded.id !== map.id && !!loaded.segSource && loaded.segSource === map.segSource
        )
    }

    /** True when this unchecked map cannot be selected under current rules. */
    isMapDisabled(map: SegObjectPropertyMap, loadedMaps: SegObjectPropertyMap[]): boolean {
        if (this.isMapLoaded(map.id, loadedMaps)) return false
        if (this.singleSelection && loadedMaps.length >= 1) return true
        return this.isMapDisabledBySegSource(map, loadedMaps)
    }

    getMapDisabledTitle(
        map: SegObjectPropertyMap,
        loadedMaps: SegObjectPropertyMap[]
    ): string | null {
        if (!this.isMapDisabled(map, loadedMaps)) return null
        if (this.singleSelection && loadedMaps.length >= 1 && !this.isMapLoaded(map.id, loadedMaps)) {
            return 'Only one intensity map can be selected for plotting'
        }
        if (!map.segSource) return null
        const conflict = loadedMaps.find(
            loaded => loaded.id !== map.id && loaded.segSource === map.segSource
        )
        const otherName = conflict?.name?.trim() || 'another map'
        return `Disabled: “${otherName}” already uses the same segmentation`
    }

    getSelectionLabel(
        loadedMaps: SegObjectPropertyMap[] | null | undefined,
        availableMaps: SegObjectPropertyMap[] | null | undefined
    ): string {
        const loaded = loadedMaps ?? []
        if (loaded.length === 0) {
            return availableMaps?.length
                ? this.singleSelection
                    ? 'Select intensity map'
                    : 'Select intensity maps'
                : 'No intensity maps'
        }
        if (loaded.length === 1) {
            return loaded[0].name ?? '1 map selected'
        }
        return `${loaded.length} maps selected`
    }

    toggleSegObjectPropertyMap(segObjectPropertyMap: SegObjectPropertyMap, wasLoaded: boolean): void {
        this.store
            .select(selectLoadedSegObjectPropertyMaps)
            .pipe(take(1))
            .subscribe(loadedMaps => {
                if (!wasLoaded && this.isMapDisabled(segObjectPropertyMap, loadedMaps)) {
                    return
                }
                this.store
                    .select(selectCaseId)
                    .pipe(take(1))
                    .subscribe((caseId: CaseId) => {
                        const remainingAfterToggle = wasLoaded
                            ? loadedMaps.filter(m => m.id !== segObjectPropertyMap.id).length
                            : loadedMaps.length + 1

                        const dispatchToggle = () => {
                            this.store.dispatch(
                                caseActions.toggleLoadedSegObjectPropertyMap({
                                    segobjectpropertymap: segObjectPropertyMap,
                                })
                            )
                            if (!wasLoaded) {
                                this.store.dispatch(
                                    caseActions.loadSegObjectPropertiesData({
                                        caseId: caseId,
                                        segobjectpropertymap: segObjectPropertyMap,
                                    })
                                )
                                if (segObjectPropertyMap.segSource) {
                                    this.store.dispatch(
                                        selectionActions.setActiveSegSource({
                                            segSource: segObjectPropertyMap.segSource,
                                        })
                                    )
                                }
                            }
                        }

                        // Clearing the last map must be instant: no spinner deferral, no waiting
                        // on in-flight plot downloads (those are cancelled by the toggle effect).
                        if (wasLoaded && remainingAfterToggle === 0) {
                            dispatchToggle()
                            return
                        }

                        // Show the plot spinner before sync catalog work blocks the main thread.
                        this.store.dispatch(plotDataActions.beginLoadingPlotData())
                        setTimeout(dispatchToggle, 0)
                    })
            })
    }
}
